import { useQuery } from '@apollo/client';
import { useCustomPush } from '@/components/CustomLink/CustomLink.hooks';
import { useUrlAndHashFromPath } from '@/hooks/useUrlAndHashFromPath';
import { ROUTES } from '@/constants/routes';
import { QUERY } from '@/constants/common';
import { Properties } from '@/api/types/searchSuggestions.types';
import { KeywordsSource } from '@/components/SearchPanel/components/SearchSuggestions.types';
import { CHECK_AD_EXISTENCE } from '@/api/queries/ads.queries';
import { isOnlyNumbers } from '@/utils/helpers';
import { CheckAdExistenceResponse } from '@/api/types/ads.types';
import { useShopSearchContext } from '@/contexts/ShopSearchContext';
import { usePageType } from '@/hooks/usePageType';
import { PageType } from '@/hooks/usePageType/usePageType.types';
import { VIP_ADS_PAGE_SLUG } from '@/hooks/usePageType/usePageType.constants';
import { PHONE_NUMBER_PROPERTY_LEGACY_ID } from '@/features/PhoneNumberSearch';

interface SearchParams {
  categoryPath?: string;
  properties?: Properties;
  keywordsSource: KeywordsSource;
  regionLegacyId?: string;
}

export function useSearch() {
  const customPush = useCustomPush();
  const { userLegacyId: shopUserLegacyId } = useShopSearchContext() || {};
  const pageType = usePageType();
  const { clearUrl, restQueryString } = useUrlAndHashFromPath([
    `${QUERY.q}[${QUERY.keywords}]`,
    QUERY.button,
    QUERY.mobileSite,
    QUERY.slug,
    QUERY.keywordsSource,
    QUERY.userId,
  ]);

  const { refetch: checkAdExistence } = useQuery<CheckAdExistenceResponse>(
    CHECK_AD_EXISTENCE,
    {
      skip: true,
      errorPolicy: 'all',
    }
  );

  async function search(searchQuery: string, params?: SearchParams) {
    if (isOnlyNumbers(searchQuery)) {
      const { data } = await checkAdExistence({
        legacyId: Number(searchQuery),
      });

      const adPath = data?.adExistence;

      if (adPath) {
        customPush(adPath);
        return;
      }
    }

    const queryParams = {
      [`${QUERY.q}[${QUERY.keywords}]`]: searchQuery,
      [QUERY.keywordsSource]: params?.keywordsSource ?? '',
    };
    const existingParams = new URLSearchParams(restQueryString);
    const phoneNumberKey = `p[${PHONE_NUMBER_PROPERTY_LEGACY_ID}]`;

    if (shopUserLegacyId) {
      queryParams[QUERY.userId] = shopUserLegacyId;
    }

    if (params?.keywordsSource === KeywordsSource.Suggestion) {
      Array.from(existingParams.keys()).forEach(key => {
        if (key.startsWith('p[') && key !== phoneNumberKey) {
          existingParams.delete(key);
        }
      });
    }

    if (params?.properties) {
      Object.entries(params.properties).forEach(([key, value]) => {
        queryParams[`p[${key}]`] = String(value);
      });
    }

    if (params?.regionLegacyId) {
      queryParams[`${QUERY.q}[${QUERY.regionId}]`] = params.regionLegacyId;
    }

    const mergedParams = new URLSearchParams(existingParams.toString());
    Object.entries(queryParams).forEach(([key, value]) => {
      mergedParams.delete(key);
      mergedParams.set(key, String(value));
    });

    const getPathname = () => {
      if (params?.categoryPath) return `/${params?.categoryPath}`;

      if (clearUrl.startsWith('/shops/')) {
        return clearUrl;
      }

      if (
        pageType === PageType.Ad ||
        clearUrl.endsWith(`/${VIP_ADS_PAGE_SLUG}`)
      ) {
        return ROUTES.ad;
      }

      return clearUrl.includes(ROUTES.ad) ? clearUrl : ROUTES.ad;
    };

    const nextPathname = getPathname();

    if (nextPathname !== window.location.pathname) {
      mergedParams.delete(phoneNumberKey);
    }

    const mergedParamsString = mergedParams.toString();
    const currentUrl = `${window.location.pathname}${window.location.search}`;
    const nextUrl = `${nextPathname}?${mergedParamsString}`;

    if (currentUrl !== nextUrl) {
      customPush(nextUrl);
    }
  }

  return search;
}
